'use client'
import React from 'react'
import { Box, ClickAwayListener, IconButton } from '@mui/material'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import RemoveIcon from '@mui/icons-material/Remove'

import DatePicker from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/CalendarWrapper/DatePicker'
import { FromToInput } from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/fromToInput'
import { MultiCitySegment } from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/hooks/search-inputs-manager'

export interface MultiCitySegmentWrapperProps {
    segment: MultiCitySegment
    segmentIndex: number
    segmentsLength: number
    segmentsWithSameAirportError: Set<number>
    isSegmentFieldActive: (segmentIndex: number, field: 'from' | 'to' | 'date') => boolean
    onSegmentFieldActivation: (segmentIndex: number, field: 'from' | 'to' | 'date') => void
    onAirportSelect: (segmentIndex: number, value: string, action: 'from' | 'to') => void
    onDateSelect: (segmentIndex: number, dates: any, range: any) => void
    onSwap: (segmentIndex: number) => void
    onRemove: (segmentIndex: number) => void
    getAirportsForSegment: (query: string, segmentIndex: number, field: 'from' | 'to') => void
    closeActiveSegmentComponent: () => void
    createSegmentClickAwayHandler: (segmentIndex: number) => (event: Event | React.SyntheticEvent) => void
    getMinDateForSegment: (segmentIndex: number) => Date | null
}

export const MultiCitySegmentWrapper: React.FC<MultiCitySegmentWrapperProps> = ({
    segment,
    segmentIndex,
    segmentsLength,
    segmentsWithSameAirportError,
    isSegmentFieldActive,
    onSegmentFieldActivation,
    onAirportSelect,
    onDateSelect,
    onSwap,
    onRemove,
    getAirportsForSegment,
    closeActiveSegmentComponent,
    createSegmentClickAwayHandler,
    getMinDateForSegment
}) => {
    // Get minDate for this segment
    const minDate = getMinDateForSegment(segmentIndex)
    return (
        <ClickAwayListener onClickAway={createSegmentClickAwayHandler(segmentIndex)}>
            <Box
                data-search-container
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    bgcolor: 'common.white',
                    position: 'relative',
                    zIndex: 1,
                    mb: 2
                }}
            >
                {/* Origin */}
                <Box sx={{
                    border: 1,
                    borderColor: 'grey.300',
                    borderRight: '1px solid rgb(229 231 235)',
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 70,
                    maxHeight: 70,
                    py: 2,
                    borderTopLeftRadius: '0.75rem',
                    borderBottomLeftRadius: '0.75rem',
                    position: 'relative',
                    flex: 1,
                    minWidth: 0
                }}>
                    <FromToInput
                        srpResultsLoading={false}
                        key={`${segment.from.value}-from-${segmentIndex}`}
                        action="from"
                        initialValue={segment.from.value || ''}
                        loading={segment.from.loading || false}
                        optionListItems={segment.from.options || []}
                        queryOptionListItems={(query) => getAirportsForSegment(query, segmentIndex, 'from')}
                        onOptionSelect={(value) => onAirportSelect(segmentIndex, value, 'from')}
                        active={isSegmentFieldActive(segmentIndex, 'from')}
                        onActivate={() => onSegmentFieldActivation(segmentIndex, 'from')}
                        onClose={closeActiveSegmentComponent}
                        hasError={segmentsWithSameAirportError.has(segmentIndex)}
                        isPopularAirports={segment.from.isPopularAirports || false}
                    />
                    
                    {/* Swap Icon - Positioned at the right border center */}
                    <Box
                        sx={{
                            position: 'absolute',
                            right: -18,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 2
                        }}
                    >
                        <IconButton 
                            sx={{ 
                                border: '1px solid rgb(229 231 235)',
                                borderRadius: '50%',
                                width: 36,
                                height: 36,
                                bgcolor: '#ffffff',
                                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                '&:hover': {
                                    bgcolor: '#f9fafb'
                                }
                            }} 
                            size="small" 
                            onClick={() => onSwap(segmentIndex)}
                        >
                            <SwapHorizIcon sx={{ fontSize: 18, color: 'grey.600' }} />
                        </IconButton>
                    </Box>
                </Box>

                {/* Destination */}
                <Box sx={{
                    border: 1,
                    borderColor: 'grey.300',
                    borderLeft: '1px solid rgb(229 231 235)',
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 70,
                    maxHeight: 70,
                    py: 2,
                    position: 'relative',
                    flex: 1,
                    minWidth: 0
                }}>
                    <FromToInput
                        key={`${segment.to.value}-to-${segmentIndex}`}
                        action="to"
                        initialValue={segment.to.value || ''}
                        loading={segment.to.loading || false}
                        optionListItems={segment.to.options || []}
                        queryOptionListItems={(query) => getAirportsForSegment(query, segmentIndex, 'to')}
                        onOptionSelect={(value) => onAirportSelect(segmentIndex, value, 'to')}
                        active={isSegmentFieldActive(segmentIndex, 'to')}
                        onActivate={() => onSegmentFieldActivation(segmentIndex, 'to')}
                        onClose={closeActiveSegmentComponent}
                        hasError={segmentsWithSameAirportError.has(segmentIndex)}
                        isPopularAirports={segment.to.isPopularAirports || false}
                    />
                </Box>

                {/* Date */}
                <Box sx={{
                    border: 1,
                    borderColor: 'grey.300',
                    borderLeft: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderTopRightRadius: segmentsLength > 2 ? '0' : '0.75rem',
                    borderBottomRightRadius: segmentsLength > 2 ? '0' : '0.75rem',
                    minHeight: 70,
                    maxHeight: 70,
                    flex: 1,
                    minWidth: 0,
                    p: 2
                }}>
                    <DatePicker
                        isRoundTrip={false}
                        range={{
                            from: segment.date || new Date(),
                            to: segment.date || new Date(),
                            enteredTo: segment.date || new Date()
                        }}
                        dateClick={(dates, range) => onDateSelect(segmentIndex, dates, range)}
                        active={isSegmentFieldActive(segmentIndex, 'date')}
                        onActivate={() => onSegmentFieldActivation(segmentIndex, 'date')}
                        onClose={closeActiveSegmentComponent}
                        showAddReturn={false}
                        minDate={minDate}
                    />
                </Box>

                {/* Remove Button - Only show when there are more than 2 segments */}
                {segmentsLength > 2 && (
                    <Box sx={{
                        border: 1,
                        borderColor: 'grey.300',
                        borderLeft: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 70,
                        maxHeight: 70,
                        p: 2,
                        borderTopRightRadius: '0.75rem',
                        borderBottomRightRadius: '0.75rem'
                    }}>
                        <IconButton
                            onClick={() => onRemove(segmentIndex)}
                            sx={{
                                bgcolor: 'transparent',
                                color: 'primary.main',
                            }}
                            size="small"
                        >
                            <RemoveIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Box>
                )}
            </Box>
        </ClickAwayListener>
    )
}

